import {create} from "zustand";
import {getStorage, setStorage} from "./util.store";

// Currency definitions - synced with API response
const currencies = [
  {
    id: "VES",
    name: "Bolívares", // Keep for backward compatibility, but components should use translation
    symbol: "Bs.", // Keep for backward compatibility, but components should use translation
    type: "fiat",
    enabled: true,
    isDefault: true,
  },
  {
    id: "USD",
    name: "US Dollars", // Keep for backward compatibility, but components should use translation
    symbol: "US$", // Disambiguated from CLP
    type: "fiat",
    enabled: true,
    isDefault: false,
  },
  {
    id: "USDT",
    name: "Tether USD", // Keep for backward compatibility, but components should use translation
    symbol: "₮", // Updated to match API response
    type: "crypto",
    enabled: true,
    isDefault: false,
  },
  {
    id: "CLP",
    name: "Peso Chileno", // Keep for backward compatibility, but components should use translation
    symbol: "CL$", // Disambiguated from USD
    type: "fiat",
    enabled: true,
    isDefault: false,
  },
  // BTC removed as requested
];

// Helper function to get selected currency from Storage
const getStoredCurrency = () => {
  try {
    const storedCurrencyId = getStorage("selectedCurrency");
    if (storedCurrencyId) {
      const currency = currencies.find(c => c.id === storedCurrencyId && c.enabled);
      if (currency) {
        return currency;
      }
    }
  } catch (_error) {}
  return currencies.find(c => c.isDefault);
};

// Helper function to save selected currency to Storage
const saveSelectedCurrency = currencyId => {
  try {
    setStorage("selectedCurrency", currencyId);
  } catch (_error) {}
};

const useCurrencyStore = create((set, get) => ({
  // State
  currencies: currencies,
  selectedCurrency: getStoredCurrency(),
  isManagementModalVisible: false,

  // Actions
  setSelectedCurrency: currencyId => {
    const currency = get().currencies.find(c => c.id === currencyId);
    if (currency?.enabled) {
      set({selectedCurrency: currency});
      saveSelectedCurrency(currencyId);
    }
  },

  toggleCurrencyEnabled: currencyId => {
    const currencies = get().currencies.map(currency => {
      if (currency.id === currencyId) {
        // Don't allow disabling the default currency
        if (currency.isDefault) {
          return currency;
        }
        return {...currency, enabled: !currency.enabled};
      }
      return currency;
    });

    set({currencies});

    // If the currently selected currency was disabled, switch to default
    const selectedCurrency = get().selectedCurrency;
    if (selectedCurrency && !currencies.find(c => c.id === selectedCurrency.id)?.enabled) {
      const defaultCurrency = currencies.find(c => c.isDefault);
      set({selectedCurrency: defaultCurrency});
      saveSelectedCurrency(defaultCurrency.id);
    }
  },

  showManagementModal: () => set({isManagementModalVisible: true}),
  hideManagementModal: () => set({isManagementModalVisible: false}),

  // Get currency conversion rate (mock implementation)
  getConversionRate: (fromCurrency, toCurrency) => {
    // This would normally fetch from an API
    const mockRates = {
      VES: {USD: 0.027, USDT: 0.027, BTC: 0.0000006},
      USD: {VES: 37.0, USDT: 1.0, BTC: 0.000023},
      USDT: {VES: 37.0, USD: 1.0, BTC: 0.000023},
      BTC: {VES: 1666666, USD: 45000, USDT: 45000},
    };

    if (fromCurrency === toCurrency) {
      return 1;
    }
    return mockRates[fromCurrency]?.[toCurrency] || 1;
  },

  convertAmount: (amount, fromCurrency, toCurrency) => {
    const rate = get().getConversionRate(fromCurrency, toCurrency);
    return amount * rate;
  },
}));

export default useCurrencyStore;
